import React from "react";
import {
  Anchor,
  Badge,
  Box,
  Button,
  Group,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import WorkInProgressCover from "../common/WorkInProgressCover";

interface PlaceholderRow {
  expanded?: boolean;
  txId: string;
  date: string;
  status: "Completed" | "Past Due" | "";
  client: string;
  staff: string;
  itemTypes: string;
  itemNames: string;
  subtotal: string;
  discount: string;
  tip: string;
  total: string;
  rewardsEarned: string;
  commission: string;
}

/** Static placeholder rows mirroring the reporting mockup. */
const ROWS: PlaceholderRow[] = [
  {
    expanded: true,
    txId: "TX-001",
    date: "11/02/2025",
    status: "Completed",
    client: "Jane D.",
    staff: "Sarah L.",
    itemTypes: "Services, Product",
    itemNames: "Haircut, Shampoo",
    subtotal: "$80",
    discount: "-$10",
    tip: "$15",
    total: "$85",
    rewardsEarned: "$80",
    commission: "$8",
  },
  {
    txId: "",
    date: "",
    status: "",
    client: "",
    staff: "Sarah L.",
    itemTypes: "Service",
    itemNames: "Haircut",
    subtotal: "$50",
    discount: "-$10",
    tip: "$15",
    total: "$55",
    rewardsEarned: "$50",
    commission: "$5",
  },
  {
    txId: "",
    date: "",
    status: "",
    client: "",
    staff: "Sarah L.",
    itemTypes: "Product",
    itemNames: "Shampoo",
    subtotal: "$30",
    discount: "$0",
    tip: "$0",
    total: "$30",
    rewardsEarned: "$30",
    commission: "$3",
  },
  {
    expanded: true,
    txId: "TX-002",
    date: "11/02/2025",
    status: "Completed",
    client: "Amy C.",
    staff: "Sarah L., Jenna M.",
    itemTypes: "Services, Product",
    itemNames: "Color, Conditioner",
    subtotal: "$145",
    discount: "$0",
    tip: "$20",
    total: "$165",
    rewardsEarned: "$145",
    commission: "$14.50",
  },
  {
    txId: "",
    date: "",
    status: "",
    client: "",
    staff: "Sarah L.",
    itemTypes: "Service",
    itemNames: "Color",
    subtotal: "$60",
    discount: "$0",
    tip: "$15",
    total: "$75",
    rewardsEarned: "$60",
    commission: "$6",
  },
  {
    txId: "",
    date: "",
    status: "",
    client: "",
    staff: "Jenna M.",
    itemTypes: "Service",
    itemNames: "Color",
    subtotal: "$60",
    discount: "$0",
    tip: "$5",
    total: "$65",
    rewardsEarned: "$60",
    commission: "$6",
  },
  {
    txId: "",
    date: "",
    status: "",
    client: "",
    staff: "Sarah L.",
    itemTypes: "Product",
    itemNames: "Conditioner",
    subtotal: "$25",
    discount: "$0",
    tip: "$0",
    total: "$25",
    rewardsEarned: "$25",
    commission: "$2.50",
  },
  {
    txId: "TX-003",
    date: "11/05/2025",
    status: "Past Due",
    client: "Lila R.",
    staff: "Jenna M.",
    itemTypes: "Service",
    itemNames: "Blowout",
    subtotal: "$70",
    discount: "$0",
    tip: "$10",
    total: "$80",
    rewardsEarned: "$70",
    commission: "$7",
  },
];

function FilterChip({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Button variant="default" disabled h="auto" py={6}>
      <Stack gap={0} align="center">
        <Text size="xs" fw={600}>
          {title}
        </Text>
        {subtitle && (
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        )}
      </Stack>
    </Button>
  );
}

/**
 * Loyalty Reporting page shell (loyalty FE1 §5) — entirely WIP-covered.
 * Mockup layout only: Transactions / Past Due / Employee Summaries tabs, the
 * filter bar, and the expandable per-transaction table (with Rewards Earned +
 * Commission columns) rendered from static placeholder rows. No endpoints.
 * Routed at /admin/loyalty/rewards/reporting and surfaced from the Reports
 * page's selection list.
 */
export default function LoyaltyReportingPage() {
  return (
    <Stack p="md" gap="md" mih="100dvh">
      <Group justify="space-between" align="center">
        <Title order={2}>Reporting</Title>
        <Anchor component={Link} to="/admin/loyalty/rewards" size="sm">
          Back to Loyalty
        </Anchor>
      </Group>

      <WorkInProgressCover>
        <Tabs value="transactions" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="transactions">Transactions</Tabs.Tab>
            <Tabs.Tab value="past-due">Past Due</Tabs.Tab>
            <Tabs.Tab value="employee-summaries">Employee Summaries</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="transactions" pt="md">
            <Stack gap="md">
              <Group gap="sm" wrap="wrap">
                <FilterChip title="Date Filter:" subtitle="Default past week" />
                <FilterChip
                  title="Select Staff:"
                  subtitle="Default all staff selected"
                />
                <FilterChip
                  title="Status:"
                  subtitle="All statuses selected by default"
                />
                <FilterChip
                  title="Customize Columns:"
                  subtitle="All columns selected by default"
                />
                <FilterChip title="Download" />
              </Group>

              <Box style={{ overflowX: "auto" }}>
                <Table withTableBorder highlightOnHover={false} miw={1100}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th />
                      <Table.Th>Transaction ID</Table.Th>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Client</Table.Th>
                      <Table.Th>Staff</Table.Th>
                      <Table.Th>Item Types</Table.Th>
                      <Table.Th>Item Names</Table.Th>
                      <Table.Th>Subtotal</Table.Th>
                      <Table.Th>Discount</Table.Th>
                      <Table.Th>Tip</Table.Th>
                      <Table.Th>Total</Table.Th>
                      <Table.Th>Rewards Earned</Table.Th>
                      <Table.Th>Commission</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {ROWS.map((row, index) => (
                      <Table.Tr key={`${row.txId || "sub"}-${index}`}>
                        <Table.Td>
                          {row.txId &&
                            (row.expanded ? (
                              <IconChevronDown size={14} />
                            ) : (
                              <IconChevronRight size={14} />
                            ))}
                        </Table.Td>
                        <Table.Td fw={row.txId ? 600 : undefined}>
                          {row.txId}
                        </Table.Td>
                        <Table.Td>{row.date}</Table.Td>
                        <Table.Td>
                          {row.status && (
                            <Badge
                              color={
                                row.status === "Completed" ? "green" : "red"
                              }
                              variant="light"
                              tt="none"
                            >
                              {row.status}
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>{row.client}</Table.Td>
                        <Table.Td>{row.staff}</Table.Td>
                        <Table.Td>{row.itemTypes}</Table.Td>
                        <Table.Td>{row.itemNames}</Table.Td>
                        <Table.Td>{row.subtotal}</Table.Td>
                        <Table.Td>{row.discount}</Table.Td>
                        <Table.Td>{row.tip}</Table.Td>
                        <Table.Td>{row.total}</Table.Td>
                        <Table.Td>{row.rewardsEarned}</Table.Td>
                        <Table.Td>{row.commission}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </WorkInProgressCover>
    </Stack>
  );
}
